// react
import React, { useEffect, useState } from 'react';
import EmptyState from '../shared/EmptyState';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

// application
import PageHeader from '../shared/PageHeader';
import theme from '../../data/theme';
import { useOrderTimer, initOrderTimer } from '../../services/orderTimer';

export default function ShopPageOrderGetById(props) {
    const { pesananslug } = props;
    const { token } = useSelector((state) => state.auth);
    const [isLoading, setIsLoading] = useState(true);
    const [kode, setKode] = useState(null);
    const [copiedField, setCopiedField] = useState(null);

    const timer = useOrderTimer(pesananslug, kode?.transaction_code, kode?.transaction_date);

    useEffect(() => {
        if (pesananslug) {
            initOrderTimer(pesananslug, kode?.transaction_code, Date.now());
        }
    }, [pesananslug, kode?.transaction_code]);

    const formatRupiah = (money) => {
        const num = parseFloat(money) || 0;
        return new Intl.NumberFormat('id-ID', {
            style: 'currency',
            currency: 'IDR',
            minimumFractionDigits: 0,
        }).format(num);
    };

    const handleCopy = (text, fieldName, label) => {
        if (!text) return;

        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(String(text));
        } else {
            const el = document.createElement('textarea');
            el.value = String(text);
            document.body.appendChild(el);
            el.select();
            document.execCommand('copy');
            document.body.removeChild(el);
        }

        setCopiedField(fieldName);
        toast.success(`${label} berhasil disalin!`);
        setTimeout(() => {
            setCopiedField(null);
        }, 2200);
    };

    const getBankBadge = (bName = '') => {
        const upper = bName.toUpperCase();
        if (upper.includes('BCA')) return { bg: '#005baa', color: '#ffffff', label: 'BCA' };
        if (upper.includes('MANDIRI')) return { bg: '#002f6c', color: '#ffffff', label: 'MANDIRI' };
        if (upper.includes('BRI')) return { bg: '#00529c', color: '#ffffff', label: 'BRI' };
        if (upper.includes('BNI')) return { bg: '#f15a24', color: '#ffffff', label: 'BNI' };
        return { bg: '#0064d2', color: '#ffffff', label: 'TRANSFER' };
    };

    useEffect(() => {
        let isMounted = true;
        const fetchOrderDetails = async () => {
            try {
                setIsLoading(true);
                const response = await axios({
                    method: 'GET',
                    url: `${process.env.REACT_APP_HOST_API}/transaction/get/${pesananslug}`,
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
                if (isMounted) {
                    setKode(response.data?.response || null);
                    setIsLoading(false);
                }
            } catch (error) {
                console.error('Error fetching order details:', error);
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };

        if (pesananslug) {
            fetchOrderDetails();
        }

        return () => {
            isMounted = false;
        };
    }, [pesananslug, token]);

    const breadcrumb = [
        { title: 'Home', url: '/' },
        { title: 'Akun Saya', url: '/account' },
        { title: 'Pemesanan Produk', url: '/account/orders' },
        { title: 'Pengecekan Pembayaran', url: '' },
    ];

    if (isLoading) {
        return (
            <div className="order-success-page-block">
                <Helmet>
                    <title>{`Pengecekan Pembayaran | ${theme.name}`}</title>
                </Helmet>
                <PageHeader breadcrumb={breadcrumb} />
                <div className="container hokky-cart-container" style={{ pointerEvents: 'none' }}>
                    <div className="order-success-card-unified">
                        {/* Header skeleton */}
                        <div className="order-header-compact">
                            <div className="skeleton-bone skeleton-bone--circle" style={{ width: 56, height: 56, margin: '0 auto 14px' }} />
                            <div className="skeleton-bone" style={{ width: 280, height: 26, margin: '0 auto 10px', borderRadius: 6 }} />
                            <div className="skeleton-bone" style={{ width: 360, height: 16, margin: '0 auto 20px', borderRadius: 4 }} />
                            <div className="order-info-pills" style={{ justifyContent: 'center' }}>
                                <div className="skeleton-bone" style={{ width: 170, height: 32, borderRadius: 20 }} />
                                <div className="skeleton-bone" style={{ width: 140, height: 32, borderRadius: 20 }} />
                                <div className="skeleton-bone" style={{ width: 210, height: 32, borderRadius: 20 }} />
                            </div>
                        </div>

                        {/* 2-column landscape body skeleton */}
                        <div className="order-body-landscape">
                            <div className="order-section-left">
                                <div className="skeleton-bone" style={{ width: 220, height: 20, marginBottom: 18, borderRadius: 4 }} />
                                <div className="order-bank-bar" style={{ background: '#f8fafc', padding: 14, borderRadius: 10 }}>
                                    <div className="skeleton-bone" style={{ width: 140, height: 26, borderRadius: 6 }} />
                                    <div className="skeleton-bone" style={{ width: 110, height: 18, borderRadius: 4 }} />
                                </div>
                                <div className="order-data-row" style={{ marginTop: 14 }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                        <div className="skeleton-bone" style={{ width: 100, height: 14, borderRadius: 4 }} />
                                        <div className="skeleton-bone" style={{ width: 180, height: 22, borderRadius: 4 }} />
                                    </div>
                                    <div className="skeleton-bone" style={{ width: 110, height: 32, borderRadius: 8 }} />
                                </div>
                                <div className="order-data-row" style={{ marginTop: 14 }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                        <div className="skeleton-bone" style={{ width: 110, height: 14, borderRadius: 4 }} />
                                        <div className="skeleton-bone" style={{ width: 150, height: 22, borderRadius: 4 }} />
                                    </div>
                                    <div className="skeleton-bone" style={{ width: 100, height: 32, borderRadius: 8 }} />
                                </div>
                            </div>
                            <div className="order-section-right">
                                <div className="skeleton-bone" style={{ width: 250, height: 20, marginBottom: 22, borderRadius: 4 }} />
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                                    <div className="skeleton-bone" style={{ width: '100%', height: 16, borderRadius: 4 }} />
                                    <div className="skeleton-bone" style={{ width: '90%', height: 16, borderRadius: 4 }} />
                                    <div className="skeleton-bone" style={{ width: '95%', height: 16, borderRadius: 4 }} />
                                </div>
                                <div className="skeleton-bone" style={{ width: '100%', height: 48, borderRadius: 12, marginTop: 24 }} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (!kode) {
        return (
            <div className="order-success-page-block">
                <Helmet>
                    <title>{`Pengecekan Pembayaran | ${theme.name}`}</title>
                </Helmet>
                <PageHeader breadcrumb={breadcrumb} />
                <div className="container hokky-cart-container">
                    <EmptyState
                        icon="receipt"
                        title="Transaksi tidak ditemukan"
                        text="Data tidak ada atau sudah kedaluwarsa."
                        actionLabel="Pesanan Saya"
                        actionTo="/account/orders"
                        actionIcon="list"
                    />
                </div>
            </div>
        );
    }

    const txCode = kode.transaction_code || '-';
    const txDate = kode.transaction_date || '-';
    const txTotal = kode.transaction_total || 0;
    const bName = kode.bank_name || 'Bank Transfer';
    const accNum = kode.account_number || '';
    const accName = kode.account_name || '';
    const bankBadge = getBankBadge(bName);

    const waText =
        `Halo Admin Hokky Bangunan, saya ingin konfirmasi pembayaran untuk pesanan:
• Kode Transaksi: #${txCode}
• Tanggal: ${txDate}
• Bank Tujuan: ${bName} (${accNum})
• Total: ${formatRupiah(txTotal)}

Berikut saya lampirkan bukti transfernya. Mohon segera dicek. Terima kasih!`;

    const whatsappUrl = `https://wa.me/6281230553236?text=${encodeURIComponent(waText)}`;

    return (
        <div className="order-success-page-block">
            <Helmet>
                <title>{`Pengecekan Pembayaran #${txCode} | ${theme.name}`}</title>
            </Helmet>

            <PageHeader breadcrumb={breadcrumb} />

            <div className="container hokky-cart-container">
                <div className="order-success-card-unified">
                    {/* Header Info Section */}
                    <div className="order-header-compact">
                        <div
                            className="order-success-icon-wrap"
                            style={{
                                background: 'linear-gradient(135deg, #0064d2 0%, #004bb5 100%)',
                                boxShadow: '0 6px 18px rgba(0, 100, 210, 0.28)',
                            }}
                        >
                            <i className="fas fa-hourglass-half" />
                        </div>
                        <h1 className="order-success-title">Proses Pengecekan Pembayaran</h1>
                        <p style={{ color: '#64748b', fontSize: '0.92rem', margin: '4px 0 16px' }}>
                            Pembayaran Anda sedang dalam antrean verifikasi oleh tim finance Hokky Bangunan
                        </p>

                        {/* Point-to-point Badges */}
                        <div className="order-info-pills">
                            <span className="order-pill">
                                No. Transaksi: <strong>#{txCode}</strong>
                                {txCode !== '-' && (
                                    <button
                                        type="button"
                                        className="btn-copy-mini"
                                        title="Salin Kode Transaksi"
                                        onClick={() => handleCopy(txCode, 'code', 'Kode transaksi')}
                                    >
                                        <i className={copiedField === 'code' ? 'fas fa-check text-success' : 'far fa-copy'} />
                                    </button>
                                )}
                            </span>
                            <span className="order-pill">
                                Tanggal: <strong>{txDate}</strong>
                            </span>
                            <span className={`order-pill ${timer.isExpired ? 'order-pill-expired' : 'order-pill-deadline'}`}>
                                <i className={timer.isExpired ? 'fas fa-ban mr-1' : 'far fa-clock mr-1'} />
                                {timer.isExpired ? 'Waktu Pembayaran Habis (Ditolak)' : `Batas Pembayaran: ${timer.formatted}`}
                            </span>
                        </div>
                    </div>

                    {/* 2-Column Landscape Body */}
                    <div className="order-body-landscape">
                        {/* LEFT COLUMN: Bank Transfer Details */}
                        <div className="order-section-left">
                            <h2 className="order-section-heading">
                                <i className="fas fa-university" /> Rekening Tujuan Transfer
                            </h2>

                            <div className="order-bank-bar">
                                <div className="bank-bar-left">
                                    <span
                                        className="bank-badge-pill"
                                        style={{ backgroundColor: bankBadge.bg, color: bankBadge.color }}
                                    >
                                        {bankBadge.label}
                                    </span>
                                    <span className="bank-bar-name">{bName}</span>
                                </div>
                            </div>

                            {/* Nomor Rekening Row */}
                            <div className="order-data-row">
                                <div className="data-row-info">
                                    <span className="data-row-label">Nomor Rekening</span>
                                    <span className="data-row-value mono">{accNum || '-'}</span>
                                </div>
                                {accNum && (
                                    <button
                                        type="button"
                                        className={`btn-copy-chip ${copiedField === 'account' ? 'copied' : ''}`}
                                        onClick={() => handleCopy(accNum, 'account', 'Nomor rekening')}
                                    >
                                        {copiedField === 'account' ? (
                                            <>
                                                <i className="fas fa-check text-success mr-1" /> Tersalin
                                            </>
                                        ) : (
                                            <>
                                                <i className="far fa-copy mr-1" /> Salin Rekening
                                            </>
                                        )}
                                    </button>
                                )}
                            </div>

                            {/* Total Pembayaran Row */}
                            <div className="order-data-row">
                                <div className="data-row-info">
                                    <span className="data-row-label">Total Pembayaran</span>
                                    <span className="data-row-value text-primary">
                                        {formatRupiah(txTotal)}
                                    </span>
                                </div>
                                <button
                                    type="button"
                                    className={`btn-copy-chip ${copiedField === 'total' ? 'copied' : ''}`}
                                    onClick={() => handleCopy(txTotal, 'total', 'Total nominal')}
                                >
                                    {copiedField === 'total' ? (
                                        <>
                                            <i className="fas fa-check text-success mr-1" /> Tersalin
                                        </>
                                    ) : (
                                        <>
                                            <i className="far fa-copy mr-1" /> Salin Jumlah
                                        </>
                                    )}
                                </button>
                            </div>

                            <div className="order-transfer-note">
                                <i className="fas fa-shield-alt" />
                                <span>Pastikan nominal yang ditransfer sesuai persis hingga 3 digit terakhir.</span>
                            </div>
                        </div>

                        {/* RIGHT COLUMN: Info Points & WhatsApp Direct Confirmation */}
                        <div className="order-section-right">
                            <h2 className="order-section-heading" style={{ "margin-bottom": "35px" }}>
                                <i className="fas fa-info-circle" /> Informasi Verifikasi Pembayaran
                            </h2>

                            <ul className="order-points-list">
                                <li>
                                    <span className="point-dot">1</span>
                                    <span>Pembayaran Anda dalam antrean pengecekan verifikasi tim finance kami dalam 1 x 24 jam kerja.</span>
                                </li>
                                <li>
                                    <span className="point-dot">2</span>
                                    <span>Ingin verifikasi lebih cepat? Kirimkan bukti transfer Anda langsung melalui WhatsApp Customer Service Hokky.</span>
                                </li>
                                <li>
                                    <span className="point-dot">3</span>
                                    <span>Setelah pembayaran diverifikasi, status transaksi otomatis diperbarui dan pesanan langsung diproses.</span>
                                </li>
                            </ul>

                            {/* Big Green Direct WhatsApp Button */}
                            {timer.isExpired ? (
                                <button
                                    type="button"
                                    disabled
                                    className="btn-whatsapp-direct"
                                    style={{
                                        marginTop: '15px',
                                        background: '#94a3b8',
                                        cursor: 'not-allowed',
                                        boxShadow: 'none',
                                    }}
                                >
                                    <i className="fas fa-ban fa-lg mr-2" /> Waktu Pembayaran Habis
                                </button>
                            ) : (
                                <a
                                    href={whatsappUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn-whatsapp-direct"
                                    style={{ marginTop: '15px' }}
                                >
                                    <i className="fab fa-whatsapp fa-lg mr-2" /> Konfirmasi via WhatsApp
                                </a>
                            )}

                            <span className="confirm-wa-sub">
                                Kirim foto struk / tangkapan layar bukti transfer melalui WhatsApp
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}